import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { atom, useAtomValue, useSetAtom, useStore } from 'jotai'
import { atomFamily, selectAtom } from 'jotai/utils'

import { sidepanelStateAtom } from '@renderer/overlay/atoms'
import { sharedCountdownChatIdsAtom } from '@renderer/overlay/hooks/for-you-linger-state'
import { useLatest } from './use-latest'

const NO_LINGERING: ReadonlySet<string> = new Set()

/**
 * Linger only locally completed reminders so they can be undone before they go.
 * To-dos retires them after idle time; Reminders waits until the user leaves
 * because its All tab retains resolved rows and would otherwise flash them back.
 */
const lingeringResolvedAtom = atom(NO_LINGERING)

/**
 * The ids lists must keep even though they're resolved. One Set instance for
 * every list rather than a per-consumer `useMemo`.
 */
export function useLingeringResolvedIds(): ReadonlySet<string> {
  return useAtomValue(lingeringResolvedAtom)
}

/**
 * How many reminder lists that drop resolved rows are on screen. Ref-counted
 * rather than a single boolean because Home's section and the Reminders View
 * hand off to each other, and for a beat both are mounted.
 */
const remindersSurfaceCountAtom = atom(0)

/** How many lists are running a countdown that will send what's ticked, which is To-dos' alone. */
const countdownListCountAtom = atom(0)

/** Whether To-dos' countdown is on screen, so an archive can wait for it to send. */
export function useHasTodosCountdown(): boolean {
  return useAtomValue(countdownListCountAtom) > 0
}

/**
 * Opening a linger is guarded rather than the read side filtered: completion is
 * reachable with no reminders list on screen at all (a meeting's For You, a chat
 * turn's artifacts, the Reminder View), and there the linger has nothing to hold
 * open. Recording one anyway strands the id until the user next opens *and*
 * leaves a reminders surface, which draws a reminder resolved minutes ago as a
 * freshly ticked row. Write-only so `useReminderActions` keeps its no-subscribe
 * contract.
 */
const startLingerAtom = atom(null, (get, set, id: string): boolean => {
  if (get(remindersSurfaceCountAtom) === 0) return false
  set(lingeringResolvedAtom, (prev) => new Set(prev).add(id))
  return get(countdownListCountAtom) > 0
})

/**
 * Per-id slice, so completing one reminder doesn't re-render every other mounted
 * cell (same reason as the staged-action families in `staged-action-local-state`).
 */
const isLingeringByIdAtom = atomFamily((id: string) => selectAtom(lingeringResolvedAtom, (ids) => ids.has(id)))
// atomFamily caches never evict on their own; these are pure derivations, so
// dropping a stale entry only costs recreating it.
isLingeringByIdAtom.setShouldRemove((createdAt) => Date.now() - createdAt > 60 * 60 * 1000)

/** Whether this reminder is being held in place after being ticked off. */
export function useIsReminderLingering(id: string): boolean {
  return useAtomValue(isLingeringByIdAtom(id))
}

export interface ReminderLingerControls {
  /**
   * Hold a just-completed reminder in place, if a list is on screen to hold it.
   * True if a countdown will send the tick when it runs out, so the caller mustn't.
   */
  start: (id: string) => boolean
  /** Un-completed before the user left — put it straight back. */
  cancel: (id: string) => void
}

export function useReminderLingerControls(): ReminderLingerControls {
  const setLingering = useSetAtom(lingeringResolvedAtom)
  const start = useSetAtom(startLingerAtom)

  const cancel = useCallback(
    (id: string) =>
      setLingering((prev) => {
        if (!prev.has(id)) return prev
        const next = new Set(prev)
        next.delete(id)
        return next
      }),
    [setLingering],
  )

  return useMemo(() => ({ start, cancel }), [start, cancel])
}

/**
 * Declares "a reminders list the user can scan is on screen here". Mounted by
 * every list that drops resolved rows — Home's section and the Reminders View.
 * Lists that keep resolved rows as a record (meeting For You, a chat turn's
 * artifacts) pass `retainsResolved` and never linger anything, so they don't
 * register.
 *
 * On screen, not merely mounted: `SidepanelContent` restyles a closed panel
 * rather than unmounting it, so both surfaces outlive the panel that shows
 * them. Both are sidepanel-hosted; a reminders list in a popout window would
 * need its own signal here.
 */
export function useRemindersSurfacePresence(): void {
  const setCount = useSetAtom(remindersSurfaceCountAtom)
  const onScreen = useAtomValue(sidepanelStateAtom) === 'open'

  useEffect(() => {
    if (!onScreen) return
    setCount((count) => count + 1)
    return () => setCount((count) => count - 1)
  }, [onScreen, setCount])
}

/**
 * Sends what's still waiting once all reminder surfaces unmount or the panel
 * closes, so leaving confirms it and nothing comes back as freshly ticked.
 */
export function useReminderLingerRelease(sendClearedTodos: SendClearedTodos): void {
  const onSurface = useAtomValue(remindersSurfaceCountAtom) > 0
  const store = useStore()
  const send = useLatest(sendClearedTodos)

  useEffect(() => {
    if (onSurface) return
    // Deferred a frame so a hand-off doesn't read as a departure: Home's section
    // unmounts as the Reminders View mounts, and the cleanup below cancels the
    // release as soon as the next surface registers.
    const frame = requestAnimationFrame(() => {
      const waiting = store.get(clearingIdsAtom)
      if (waiting.size > 0) send.current(waiting)
    })
    return () => cancelAnimationFrame(frame)
  }, [onSurface, send, store])
}

const LINGER_MS = 5000

/** The countdown's checks and the Undo button's sliding fill share this interval. */
export const LINGER_CHECK_MS = 100

/** While the user is busy the countdown crawls at this share of real time, 20s from full to empty. */
const BUSY_COUNTDOWN_RATE = 0.25

const BUSY_WINDOW_MS = 500

/** How long a leaving row takes to close, matching `TodoRowPresence`'s transition. */
const ROW_LEAVE_MS = 200

const ACTIVITY_EVENTS = ['pointermove', 'wheel', 'keydown'] as const

/** The share of the countdown used up, from 0 to 1, or null while nothing is counting down. */
const lingerCountdownSpentAtom = atom<number | null>(null)

export function useLingerCountdownSpent(): number | null {
  return useAtomValue(lingerCountdownSpentAtom)
}

/** Sends what the countdown held back, and lets go of it. */
export type SendClearedTodos = (ids: ReadonlySet<string>) => void

/** Everything To-dos' shared countdown clears at once: ticked reminders and archived chats. */
const clearingIdsAtom = atom(
  (get): ReadonlySet<string> => new Set([...get(lingeringResolvedAtom), ...get(sharedCountdownChatIdsAtom)]),
)

/** Checkboxes retain focus after a tick but must not block row retirement. */
function isEditingList(list: HTMLElement): boolean {
  const active = document.activeElement
  const hasCaret =
    active instanceof HTMLElement &&
    list.contains(active) &&
    (active.isContentEditable || active.matches('textarea, input:not([type="checkbox"])'))
  return hasCaret || document.querySelector('[role="dialog"], [role="menu"]') !== null
}

interface PointerPosition {
  x: number
  y: number
}

/**
 * Where the pointer last was, kept outside the countdown's effect because a tick
 * restarts that effect with the pointer resting on the mark it just pressed.
 */
function useLastPointerPosition(): RefObject<PointerPosition | null> {
  const position = useRef<PointerPosition | null>(null)

  useEffect(() => {
    const notePosition = (event: PointerEvent) => {
      position.current = { x: event.clientX, y: event.clientY }
    }
    const forgetPosition = () => {
      position.current = null
    }
    const root = document.documentElement
    window.addEventListener('pointermove', notePosition, { passive: true })
    window.addEventListener('pointerdown', notePosition, { passive: true })
    root.addEventListener('pointerleave', forgetPosition)
    return () => {
      window.removeEventListener('pointermove', notePosition)
      window.removeEventListener('pointerdown', notePosition)
      root.removeEventListener('pointerleave', forgetPosition)
    }
  }, [])

  return position
}

/** A to-do's mark and the panel's toasts, where the undo toast shows: the two places a tick gets taken back. */
const UNDO_TARGETS = '[data-slot="todo-mark-target"], [data-slot="sidepanel-toasts"]'

/**
 * The pointer is somewhere a tick gets taken back. Hit tested from its last
 * position, so a pointer at rest counts as much as a moving one.
 */
function isOverUndoTarget(position: PointerPosition | null): boolean {
  if (!position) return false
  return document.elementFromPoint(position.x, position.y)?.closest(UNDO_TARGETS) != null
}

interface IdleLingerRetirement {
  listRef: (list: HTMLElement | null) => void
  /** Rows closing up on their way out, for `ROW_LEAVE_MS` before they drop from the list. */
  leavingIds: ReadonlySet<string>
}

/**
 * One countdown for everything ticked, so it all leaves together. Activity slows
 * it without resetting it, and hovering a to-do's mark or the undo status holds it
 * still; retirement waits for idle so rows cannot move under an active pointer or
 * editor. Tick and undo reset it. Nothing reaches the server until it runs out or
 * the list goes, so an undo before then has nothing to take back.
 */
export function useRetireLingersWhenIdle(sendClearedTodos: SendClearedTodos): IdleLingerRetirement {
  const [list, setList] = useState<HTMLElement | null>(null)
  const clearingIds = useAtomValue(clearingIdsAtom)
  const setCountdownListCount = useSetAtom(countdownListCountAtom)
  const store = useStore()
  const send = useLatest(sendClearedTodos)
  const [leavingIds, setLeavingIds] = useState(NO_LINGERING)
  const setCountdownSpent = useSetAtom(lingerCountdownSpentAtom)
  const pointerPosition = useLastPointerPosition()

  useEffect(() => {
    const waitingIds = [...clearingIds].filter((id) => !leavingIds.has(id))
    if (!list || waitingIds.length === 0) return
    let remainingMs = LINGER_MS
    let lastCheckAt = Date.now()
    let lastActivityAt = -Infinity
    setCountdownSpent(0)
    const noteActivity = () => {
      lastActivityAt = Date.now()
    }
    const check = setInterval(() => {
      const now = Date.now()
      const overUndoTarget = isOverUndoTarget(pointerPosition.current)
      const busy = now - lastActivityAt < BUSY_WINDOW_MS || isEditingList(list)
      const rate = overUndoTarget ? 0 : busy ? BUSY_COUNTDOWN_RATE : 1
      remainingMs = Math.max(0, remainingMs - (now - lastCheckAt) * rate)
      lastCheckAt = now
      if (remainingMs === 0 && !busy && !overUndoTarget) setLeavingIds(new Set(waitingIds))
      else setCountdownSpent(1 - remainingMs / LINGER_MS)
    }, LINGER_CHECK_MS)
    for (const event of ACTIVITY_EVENTS) window.addEventListener(event, noteActivity, { passive: true })
    return () => {
      clearInterval(check)
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, noteActivity)
      setCountdownSpent(null)
    }
  }, [list, clearingIds, leavingIds, setCountdownSpent, pointerPosition])

  useEffect(() => {
    if (leavingIds.size === 0) return
    const timer = setTimeout(() => {
      send.current(leavingIds)
      setLeavingIds(NO_LINGERING)
    }, ROW_LEAVE_MS)
    return () => clearTimeout(timer)
  }, [leavingIds, send])

  // Going to another View ends the countdown, and with it the undo.
  useEffect(() => {
    setCountdownListCount((count) => count + 1)
    return () => {
      setCountdownListCount((count) => count - 1)
      const waiting = store.get(clearingIdsAtom)
      if (waiting.size > 0) send.current(waiting)
    }
  }, [send, setCountdownListCount, store])

  return { listRef: setList, leavingIds }
}
